// src/hooks/useAdminAuth.js
import { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

/**
 * A custom hook to manage authentication and admin authorization.
 * It checks if a user is signed in and if they have admin privileges
 * based on both their Firestore document role and custom token claims.
 * 
 * NOTE: This hook does NOT auto-redirect. Screens should handle their own
 * redirect logic based on their requirements:
 * 
 * - Public screens (EventDetailScreen): Show content to everyone
 * - Auth-required screens (CreateEventsScreen): Check isLoggedIn, redirect if false
 * - Admin-only screens (AdminDashboard): Check isAdmin, redirect if false
 *
 * @returns {{
 *   user: import('firebase/auth').User | null,
 *   isAdmin: boolean,
 *   isLoading: boolean,
 *   isLoggedIn: boolean
 * }}
 */
export function useAdminAuth() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser) {
        // No user signed in - just update state, DON'T redirect
        setUser(null);
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      setUser(authUser);

      try {
        // Check both custom claims and the Firestore document for the admin role
        const tokenResult = await authUser.getIdTokenResult(true);
        const hasAdminClaim = tokenResult.claims.role === 'admin';

        const userDocRef = doc(db, 'users', authUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        const hasAdminRoleInDoc = userDocSnap.exists() && userDocSnap.data()?.role === 'admin';

        setIsAdmin(hasAdminClaim || hasAdminRoleInDoc);
      } catch (error) {
        console.error("Error verifying admin status:", error);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return { 
    user, 
    isAdmin, 
    isLoading,
    isLoggedIn: !!user,
  };
}

export default useAdminAuth;